/**
 * @fileoverview Ultra-reactive mobile frontend component for stadium fans playing "Farmeando Aura" / "Fighters".
 * Exact 1:1 visual replica of ADSL FarmeandoAuraFan with Interaktia-V2 SignalR multi-room integration.
 * Provides high-speed touch event processing, batched WebSocket tap transmission (every 100ms),
 * dynamic theme styling, side selection, real-time state synchronization, and Screen WakeLock management.
 * 
 * @module components/Dashboard/Sections/FightersFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ConnectWebSocketForFightersFan,
    DisconnectWebSocketForFightersFan
} from '../../../utilities/WS/FightersFanWS';
import {
    GetConfig,
    SendTapBatchBL,
    RegisterFanBL,
    UnregisterFanBL,
    GetFanSessionData,
    SaveFanSessionData,
    ClearFanSessionData
} from '../../../utilities/BL/Fighters';

/**
 * Key stored in sessionStorage to remember the fan's chosen team side.
 * @constant {string}
 */
const SESSION_STORAGE_SIDE_KEY = 'farmeandoAura.selectedSide';
const SESSION_STORAGE_SIDE_KEY_ALT = 'fighters.selectedSide';

/**
 * Tap flush interval threshold in milliseconds for batched WebSocket dispatch.
 * @constant {number}
 */
const BATCH_DISPATCH_INTERVAL_MILLISECONDS = 100;

/**
 * Maximum taps threshold to trigger an immediate batch flush before the timer expires.
 * @constant {number}
 */
const MAXIMUM_TAPS_PER_IMMEDIATE_BATCH = 6;

/**
 * Collection of emojis for floating aura particles spawned on touch interactions.
 * @constant {Array<string>}
 */
const AURA_FLOATING_EMOJIS = ['⚡', '🔥', '✨', '💥', '🌟', '💫', '☄️'];

/**
 * Default fallback colors when no theme is provided.
 * @constant {Object}
 */
const DEFAULT_THEME_COLORS = {
    player1Color: '#00ffcc',
    player2Color: '#ff0055',
    fanBackgroundColor: '#0a0a14',
    fanTextColor: '#ffffff'
};

/**
 * FightersFan / FarmeandoAuraFan Component (100% ADSL Replica)
 * 
 * @function FightersFan
 * @param {Object} [props] - Component properties.
 * @param {Object} [props.matchState] - Real-time match state from router.
 * @param {Object} [props.config] - Theme/game configuration.
 * @param {string} [props.winner] - Match winner ('J1' | 'J2' | 'Empate').
 * @param {string} [props.roomId] - Room GUID identifier.
 * @returns {React.ReactElement} The rendered mobile fan interface.
 */
export const FightersFan = ({
    matchState: propMatchState,
    config: propConfig,
    winner: propWinner,
    roomId: propRoomId
}) => {
    // =========================================================================
    // 1. DETECCIÓN DE SALA Y BANDO DE SESIÓN
    // =========================================================================
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = propRoomId
        || searchParams.get('roomId')
        || propMatchState?.roomId
        || (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '')
        || '';

    const [selectedPlayerSide, setSelectedPlayerSide] = useState(() => {
        const sessionData = GetFanSessionData(activeRoomId);
        if (sessionData?.playerChoice) {
            const normalized = sessionData.playerChoice === 'player1' ? 'J1' : sessionData.playerChoice === 'player2' ? 'J2' : sessionData.playerChoice;
            return normalized;
        }
        if (typeof sessionStorage !== 'undefined') {
            const savedSide = sessionStorage.getItem(SESSION_STORAGE_SIDE_KEY) || sessionStorage.getItem(SESSION_STORAGE_SIDE_KEY_ALT);
            if (savedSide) return savedSide;
        }
        if (typeof localStorage !== 'undefined') {
            const localChoice = localStorage.getItem('fighters_player_choice');
            if (localChoice) {
                return localChoice === 'player1' ? 'J1' : localChoice === 'player2' ? 'J2' : localChoice;
            }
        }
        return '';
    });

    // =========================================================================
    // 2. ESTADOS DE RED Y CONECTIVIDAD
    // =========================================================================
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('idle');

    // =========================================================================
    // 3. ESTADOS DE LA PARTIDA Y SINCRONIZACIÓN CON EL SERVIDOR
    // =========================================================================
    const [currentEngineState, setCurrentEngineState] = useState(() => {
        const rawPhase = propMatchState?.phase || propMatchState?.state || propConfig?.phase || 'idle';
        const p = String(rawPhase).toLowerCase();
        if (p === 'playing' || p === 'running') return 'playing';
        if (p === 'countdown') return 'countdown';
        if (p === 'finished') return 'finished';
        if (p === 'paused') return 'paused';
        if (p === 'game_ready' || p === 'gameready') return 'game_ready';
        if (p === 'cover') return 'cover';
        return 'idle';
    });

    const [roundCountdownValue, setRoundCountdownValue] = useState(3);
    const [roundWinnerResult, setRoundWinnerResult] = useState(() => {
        if (propWinner) return propWinner === 'player1' ? 'J1' : propWinner === 'player2' ? 'J2' : propWinner;
        if (propMatchState?.winner) return propMatchState.winner === 'player1' ? 'J1' : propMatchState.winner === 'player2' ? 'J2' : propMatchState.winner;
        return null;
    });
    const [activeThemeData, setActiveThemeData] = useState(() => {
        return propConfig || propMatchState?.theme || null;
    });

    // Sincronización directa con props entrantes
    useEffect(() => {
        if (propConfig && typeof propConfig === 'object') {
            setActiveThemeData((prev) => ({ ...(prev || {}), ...propConfig }));
        }
    }, [propConfig]);

    useEffect(() => {
        if (propMatchState) {
            const rawPhase = propMatchState.phase || propMatchState.state;
            if (rawPhase) {
                const p = String(rawPhase).toLowerCase();
                setCurrentEngineState(p === 'playing' || p === 'running' ? 'playing' : p === 'countdown' ? 'countdown' : p === 'finished' ? 'finished' : p === 'paused' ? 'paused' : p === 'game_ready' ? 'game_ready' : p === 'cover' ? 'cover' : 'idle');
            }
            if (propMatchState.theme && typeof propMatchState.theme === 'object') {
                setActiveThemeData((prev) => ({ ...(prev || {}), ...propMatchState.theme }));
            }
            if (propMatchState.winner !== undefined && propMatchState.winner !== null) {
                const w = propMatchState.winner === 'player1' ? 'J1' : propMatchState.winner === 'player2' ? 'J2' : propMatchState.winner;
                setRoundWinnerResult(w);
            }
        }
    }, [propMatchState]);

    useEffect(() => {
        if (propWinner !== undefined && propWinner !== null) {
            const w = propWinner === 'player1' ? 'J1' : propWinner === 'player2' ? 'J2' : propWinner;
            setRoundWinnerResult(w);
            setCurrentEngineState('finished');
        }
    }, [propWinner]);

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA ALTO RENDIMIENTO (BATCHING & WAKELOCK)
    // =========================================================================
    const tapAccumulatorBufferRef = useRef({
        accumulatedTapCount: 0,
        dispatchTimerIdentifier: null,
        isDispatching: false
    });

    const screenWakeLockSentinelRef = useRef(null);
    const activeCombatAvatarImageRef = useRef(null);
    const particlesContainerRef = useRef(null);

    // =========================================================================
    // 5. GESTIÓN DEL SCREEN WAKELOCK (MANTENER PANTALLA ENCENDIDA)
    // =========================================================================
    const requestScreenWakeLock = useCallback(async () => {
        try {
            if (typeof navigator !== 'undefined' && 'wakeLock' in navigator && !screenWakeLockSentinelRef.current) {
                const wakeLockSentinel = await navigator.wakeLock.request('screen');
                screenWakeLockSentinelRef.current = wakeLockSentinel;

                wakeLockSentinel.addEventListener('release', () => {
                    screenWakeLockSentinelRef.current = null;
                });
            }
        } catch (wakeLockError) {
            // WakeLock non-blocking
        }
    }, []);

    const releaseScreenWakeLock = useCallback(async () => {
        try {
            if (screenWakeLockSentinelRef.current) {
                await screenWakeLockSentinelRef.current.release();
                screenWakeLockSentinelRef.current = null;
            }
        } catch (releaseError) {
            screenWakeLockSentinelRef.current = null;
        }
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible' && selectedPlayerSide && currentEngineState !== 'finished') {
                requestScreenWakeLock();
            }
        };

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
    }, [selectedPlayerSide, currentEngineState, requestScreenWakeLock]);

    // =========================================================================
    // 6. CARGA DE CONFIGURACIÓN Y TEMA INICIAL
    // =========================================================================
    useEffect(() => {
        let isMounted = true;
        const loadInitialThemeConfiguration = async () => {
            try {
                const configurationResponse = await GetConfig(activeRoomId);
                if (isMounted && configurationResponse.success && configurationResponse.data) {
                    if (configurationResponse.data.activeTheme) {
                        setActiveThemeData(configurationResponse.data.activeTheme);
                    }
                    if (configurationResponse.data.currentState) {
                        const rawState = configurationResponse.data.currentState;
                        const p = String(rawState).toLowerCase();
                        setCurrentEngineState(p === 'playing' || p === 'running' ? 'playing' : p === 'countdown' ? 'countdown' : p === 'finished' ? 'finished' : p === 'paused' ? 'paused' : p === 'game_ready' ? 'game_ready' : p === 'cover' ? 'cover' : 'idle');
                    }
                }
            } catch (themeLoadingError) {
                // Non-blocking fallback
            }
        };

        loadInitialThemeConfiguration();
        return () => {
            isMounted = false;
        };
    }, [activeRoomId]);

    // =========================================================================
    // 7. WEBSOCKET / SIGNALR COMUNICACIÓN Y PROCESAMIENTO DE MENSAJES
    // =========================================================================
    const flushAccumulatedTapsToServer = useCallback(() => {
        const tapBuffer = tapAccumulatorBufferRef.current;

        if (tapBuffer.dispatchTimerIdentifier) {
            clearTimeout(tapBuffer.dispatchTimerIdentifier);
            tapBuffer.dispatchTimerIdentifier = null;
        }

        if (tapBuffer.accumulatedTapCount <= 0 || !selectedPlayerSide) {
            return;
        }

        const tapsToTransmit = tapBuffer.accumulatedTapCount;
        tapBuffer.accumulatedTapCount = 0;

        const normalizedSide = (selectedPlayerSide === 'J1' || selectedPlayerSide === 'player1') ? 'player1' : 'player2';

        // Despachar mediante BL
        SendTapBatchBL(activeRoomId, normalizedSide, tapsToTransmit);
    }, [activeRoomId, selectedPlayerSide]);

    const handleIncomingWebSocketMessage = useCallback((receivedMessage) => {
        if (!receivedMessage || !receivedMessage.type) {
            return;
        }

        const normalizedMessageType = receivedMessage.type.toLowerCase();
        const incomingData = receivedMessage.data || receivedMessage.payload || {};

        switch (normalizedMessageType) {
            case 'aura_snapshot':
            case 'fighters_snapshot':
            case 'snapshot': {
                const snapshotState = receivedMessage.state || incomingData;
                if (snapshotState.state || snapshotState.phase) {
                    const rawP = snapshotState.state || snapshotState.phase;
                    const p = String(rawP).toLowerCase();
                    setCurrentEngineState(p === 'playing' || p === 'running' ? 'playing' : p === 'countdown' ? 'countdown' : p === 'finished' ? 'finished' : p === 'paused' ? 'paused' : p === 'game_ready' ? 'game_ready' : p === 'cover' ? 'cover' : 'idle');
                }
                if (snapshotState.currentTheme || snapshotState.theme) {
                    setActiveThemeData(snapshotState.currentTheme || snapshotState.theme);
                }
                if (snapshotState.winner) {
                    const w = (snapshotState.winner === 'player1' || snapshotState.winner === 'J1') ? 'J1'
                        : ((snapshotState.winner === 'player2' || snapshotState.winner === 'J2') ? 'J2'
                        : 'Empate');
                    setRoundWinnerResult(w);
                }
                break;
            }

            case 'aura_show_cover':
            case 'fighters_cover_shown': {
                setCurrentEngineState('cover');
                const t = receivedMessage.state?.currentTheme || incomingData.theme || incomingData.currentTheme;
                if (t) setActiveThemeData(t);
                setRoundWinnerResult(null);
                break;
            }

            case 'aura.prepare':
            case 'aura_show_game':
            case 'fighters_game_scene_shown':
            case 'fighters_game_ready': {
                setCurrentEngineState('game_ready');
                const t = receivedMessage.state?.currentTheme || incomingData.theme || incomingData.currentTheme;
                if (t) setActiveThemeData(t);
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_countdown':
            case 'fighters_countdown':
            case 'fighters_countdown_started': {
                setCurrentEngineState('countdown');
                const countVal = receivedMessage.countdown !== undefined ? receivedMessage.countdown
                    : (receivedMessage.count !== undefined ? receivedMessage.count
                    : (receivedMessage.secondsRemaining !== undefined ? receivedMessage.secondsRemaining
                    : (incomingData.countdown !== undefined ? incomingData.countdown
                    : (incomingData.count !== undefined ? incomingData.count
                    : (incomingData.secondsRemaining !== undefined ? incomingData.secondsRemaining : 3)))));
                setRoundCountdownValue(countVal);
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_round_started':
            case 'fighters_round_started': {
                setCurrentEngineState('playing');
                setRoundWinnerResult(null);
                break;
            }

            case 'aura_telemetry':
            case 'fighters_telemetry': {
                if (currentEngineState !== 'playing') {
                    setCurrentEngineState('playing');
                }
                break;
            }

            case 'aura_paused':
            case 'fighters_paused': {
                setCurrentEngineState('paused');
                break;
            }

            case 'aura_resumed':
            case 'fighters_resumed': {
                setCurrentEngineState('playing');
                break;
            }

            case 'aura_game_over':
            case 'fighters_game_over': {
                flushAccumulatedTapsToServer();
                setCurrentEngineState('finished');
                const rawWin = receivedMessage.winner || incomingData.winner || 'Empate';
                const finalWinner = (rawWin === 'player1' || rawWin === 'J1') ? 'J1'
                    : ((rawWin === 'player2' || rawWin === 'J2') ? 'J2'
                    : 'Empate');
                setRoundWinnerResult(finalWinner);
                releaseScreenWakeLock();
                break;
            }

            case 'aura_reset':
            case 'fighters_game_reset': {
                flushAccumulatedTapsToServer();
                setCurrentEngineState('idle');
                setRoundWinnerResult(null);
                break;
            }

            default:
                break;
        }
    }, [flushAccumulatedTapsToServer, releaseScreenWakeLock]);

    // WebSocket lifecycle connection effect
    useEffect(() => {
        if (!selectedPlayerSide) {
            DisconnectWebSocketForFightersFan();
            return;
        }

        ConnectWebSocketForFightersFan(
            selectedPlayerSide,
            handleIncomingWebSocketMessage,
            (newConnectionStatus) => setWebSocketConnectionStatus(newConnectionStatus),
            activeRoomId
        );

        if (currentEngineState !== 'finished') {
            requestScreenWakeLock();
        }

        return () => {
            flushAccumulatedTapsToServer();
            DisconnectWebSocketForFightersFan();
            releaseScreenWakeLock();

            const tapBuffer = tapAccumulatorBufferRef.current;
            if (tapBuffer.dispatchTimerIdentifier) {
                clearTimeout(tapBuffer.dispatchTimerIdentifier);
                tapBuffer.dispatchTimerIdentifier = null;
            }
        };
    }, [selectedPlayerSide, activeRoomId, handleIncomingWebSocketMessage, flushAccumulatedTapsToServer, requestScreenWakeLock, releaseScreenWakeLock, currentEngineState]);

    // =========================================================================
    // 8. EFECTOS VISUALES DIRECTOS EN DOM (PARTÍCULAS Y RIPPLES)
    // =========================================================================
    const spawnTapRippleEffect = (coordinateX, coordinateY) => {
        if (typeof document === 'undefined') return;
        const rippleElement = document.createElement('div');
        rippleElement.className = 'aura-fan__tap-ripple';
        rippleElement.style.left = `${coordinateX}px`;
        rippleElement.style.top = `${coordinateY}px`;

        const targetContainer = particlesContainerRef.current || document.body;
        targetContainer.appendChild(rippleElement);

        setTimeout(() => {
            rippleElement.remove();
        }, 320);
    };

    const spawnFloatingAuraParticle = (coordinateX, coordinateY) => {
        if (typeof document === 'undefined') return;
        const particleElement = document.createElement('div');
        particleElement.className = 'aura-fan__floating-particle';
        particleElement.innerText = AURA_FLOATING_EMOJIS[Math.floor(Math.random() * AURA_FLOATING_EMOJIS.length)];

        const randomizedDeltaX = (Math.random() - 0.5) * 160;
        const randomizedDeltaY = -(Math.random() * 180 + 80);
        const randomizedRotation = (Math.random() - 0.5) * 90;

        particleElement.style.setProperty('--target-x', `${randomizedDeltaX}px`);
        particleElement.style.setProperty('--target-y', `${randomizedDeltaY}px`);
        particleElement.style.setProperty('--target-rotation', `${randomizedRotation}deg`);
        particleElement.style.left = `${coordinateX}px`;
        particleElement.style.top = `${coordinateY}px`;

        const targetContainer = particlesContainerRef.current || document.body;
        targetContainer.appendChild(particleElement);

        setTimeout(() => {
            particleElement.remove();
        }, 750);
    };

    // =========================================================================
    // 9. CONTROLADOR DE TAPS ULTRA-REACTIVO
    // =========================================================================
    const handlePointerDownTap = (pointerInteractionEvent) => {
        if (currentEngineState !== 'playing' || !selectedPlayerSide) {
            return;
        }

        // Haptic feedback
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator && typeof navigator.vibrate === 'function') {
            try {
                navigator.vibrate(15);
            } catch (vibrationError) {
                // Safe ignore
            }
        }

        const touchCoordinateX = pointerInteractionEvent.clientX !== undefined
            ? pointerInteractionEvent.clientX
            : (pointerInteractionEvent.touches && pointerInteractionEvent.touches[0] ? pointerInteractionEvent.touches[0].clientX : (typeof window !== 'undefined' ? window.innerWidth / 2 : 200));

        const touchCoordinateY = pointerInteractionEvent.clientY !== undefined
            ? pointerInteractionEvent.clientY
            : (pointerInteractionEvent.touches && pointerInteractionEvent.touches[0] ? pointerInteractionEvent.touches[0].clientY : (typeof window !== 'undefined' ? window.innerHeight / 2 : 300));

        // Immediate visual DOM effects
        spawnTapRippleEffect(touchCoordinateX, touchCoordinateY);
        spawnFloatingAuraParticle(touchCoordinateX, touchCoordinateY);

        // Mascot aura reaction animation
        if (activeCombatAvatarImageRef.current) {
            activeCombatAvatarImageRef.current.classList.add('aura-fan__combat__avatar-img--hit');
            setTimeout(() => {
                if (activeCombatAvatarImageRef.current) {
                    activeCombatAvatarImageRef.current.classList.remove('aura-fan__combat__avatar-img--hit');
                }
            }, 60);
        }

        // Increment tap batch accumulator
        const tapBuffer = tapAccumulatorBufferRef.current;
        tapBuffer.accumulatedTapCount += 1;

        if (tapBuffer.accumulatedTapCount >= MAXIMUM_TAPS_PER_IMMEDIATE_BATCH) {
            flushAccumulatedTapsToServer();
            return;
        }

        if (!tapBuffer.dispatchTimerIdentifier) {
            tapBuffer.dispatchTimerIdentifier = setTimeout(
                flushAccumulatedTapsToServer,
                BATCH_DISPATCH_INTERVAL_MILLISECONDS
            );
        }
    };

    // =========================================================================
    // 10. ACCIONES DE USUARIO: SELECCIÓN Y SALIDA
    // =========================================================================
    const handlePickSide = (chosenSide) => {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(SESSION_STORAGE_SIDE_KEY, chosenSide);
            sessionStorage.setItem(SESSION_STORAGE_SIDE_KEY_ALT, chosenSide);
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('fighters_player_choice', chosenSide === 'J1' ? 'player1' : 'player2');
        }
        SaveFanSessionData(activeRoomId, chosenSide === 'J1' ? 'player1' : 'player2');
        RegisterFanBL(activeRoomId, chosenSide === 'J1' ? 'player1' : 'player2');
        setSelectedPlayerSide(chosenSide);
        requestScreenWakeLock();
    };

    const handleSwitchSideOrLogout = () => {
        flushAccumulatedTapsToServer();
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SESSION_STORAGE_SIDE_KEY);
            sessionStorage.removeItem(SESSION_STORAGE_SIDE_KEY_ALT);
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('fighters_player_choice');
        }
        ClearFanSessionData(activeRoomId);
        UnregisterFanBL(activeRoomId);
        setSelectedPlayerSide('');
        releaseScreenWakeLock();
        DisconnectWebSocketForFightersFan();
    };

    // =========================================================================
    // 11. HELPERS VISUALES (MEDIA Y COLORES)
    // =========================================================================
    const getResolvedMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) return mediaPath;
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedPath}`;
    };

    const teamPrimaryColor = selectedPlayerSide === 'J2'
        ? (activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color)
        : (activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color);

    const teamAvatarUrl = selectedPlayerSide === 'J2'
        ? (activeThemeData?.player2Avatar ? getResolvedMediaUrl(activeThemeData.player2Avatar) : '')
        : (activeThemeData?.player1Avatar ? getResolvedMediaUrl(activeThemeData.player1Avatar) : '');

    const teamWinnerAvatarUrl = selectedPlayerSide === 'J2'
        ? (activeThemeData?.player2Winner ? getResolvedMediaUrl(activeThemeData.player2Winner) : '')
        : (activeThemeData?.player1Winner ? getResolvedMediaUrl(activeThemeData.player1Winner) : '');

    // =========================================================================
    // 12. RENDERIZADOR DEL FONDO DINÁMICO
    // =========================================================================
    const renderFanThemeBackground = () => {
        const backgroundType = activeThemeData?.fanBackgroundType || 'color';
        const backgroundColor = activeThemeData?.fanBackgroundColor || DEFAULT_THEME_COLORS.fanBackgroundColor;
        const backgroundMediaUrl = activeThemeData?.fanBackgroundUrl ? getResolvedMediaUrl(activeThemeData.fanBackgroundUrl) : null;

        if (backgroundType === 'video' && backgroundMediaUrl) {
            return (
                <div className="aura-fan__background">
                    <video
                        src={backgroundMediaUrl}
                        className="aura-fan__background--video"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                    <div className="aura-fan__background--overlay" />
                </div>
            );
        }

        if (backgroundType === 'image' && backgroundMediaUrl) {
            return (
                <div className="aura-fan__background">
                    <img
                        src={backgroundMediaUrl}
                        className="aura-fan__background--image"
                        alt="Tema Dinámico Farmeando Aura"
                    />
                    <div className="aura-fan__background--overlay" />
                </div>
            );
        }

        return (
            <div className="aura-fan__background">
                <div
                    className="aura-fan__background--color"
                    style={{ backgroundColor: backgroundColor }}
                />
                <div className="aura-fan__background--overlay" />
            </div>
        );
    };

    // =========================================================================
    // 13. DETERMINACIÓN DEL ESTADO VISUAL EFECTIVO
    // =========================================================================
    let currentEffectiveView = 'waiting';

    if (!selectedPlayerSide) {
        currentEffectiveView = 'selecting_side';
    } else if (currentEngineState === 'finished') {
        currentEffectiveView = 'finished';
    } else if (currentEngineState === 'countdown') {
        currentEffectiveView = 'countdown';
    } else if (currentEngineState === 'playing') {
        currentEffectiveView = 'playing';
    } else {
        currentEffectiveView = 'waiting';
    }

    // =========================================================================
    // 14. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <div className="aura-fan">
            {/* Capa de fondo dinámico */}
            {renderFanThemeBackground()}

            {/* Contenedor flotante para partículas y ripples */}
            <div ref={particlesContainerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 40 }} />

            {/* Cabecera Superior */}
            <header className="aura-fan__header">
                <div className="aura-fan__header__logo-container">
                    {activeThemeData?.brandLogoUrl ? (
                        <img
                            src={getResolvedMediaUrl(activeThemeData.brandLogoUrl)}
                            alt="Logo Marca"
                            className="aura-fan__header__logo"
                        />
                    ) : (
                        <img
                            src="/img/page/xolos-five.png"
                            alt="Logo Xolos"
                            className="aura-fan__header__logo"
                        />
                    )}
                </div>

                <div className="aura-fan__header__controls">
                    {selectedPlayerSide && (
                        <span
                            className="aura-fan__header__badge"
                            style={{
                                backgroundColor: '#fff',
                                color: '#000'
                            }}
                        >
                            {selectedPlayerSide === 'J1' ? 'Jugador 1' : 'Jugador 2'}
                        </span>
                    )}

                    {selectedPlayerSide && (
                        <button
                            type="button"
                            className="aura-fan__header__exit-button"
                            onClick={handleSwitchSideOrLogout}
                            title="Cambiar de equipo o salir"
                        >
                            <i className="bi bi-box-arrow-left"></i>
                        </button>
                    )}
                </div>
            </header>

            {/* ============================================================= */}
            {/* VISTA 1: SELECCIÓN DE BANDO                                   */}
            {/* ============================================================= */}
            {currentEffectiveView === 'selecting_side' && (
                <main className="aura-fan__select-side">
                    <h2 className="aura-fan__select-side__hero-title">Elige a tu personaje</h2>

                    <div className="aura-fan__select-side__cards-container">
                        {/* Botón Bando Jugador 1 (Local) */}
                        <button
                            type="button"
                            className="aura-fan__select-side__card aura-fan__select-side__card--p1"
                            onClick={() => handlePickSide('J1')}
                        >
                            {activeThemeData?.player1Avatar ? (
                                <img
                                    src={getResolvedMediaUrl(activeThemeData.player1Avatar)}
                                    alt="Avatar Jugador 1"
                                    className="aura-fan__select-side__card__avatar"
                                    onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                                />
                            ) : (
                                <div
                                    className="aura-fan__select-side__card__avatar d-flex align-items-center justify-content-center rounded-circle"
                                    style={{ backgroundColor: activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color }}
                                >
                                    <i className="bi bi-person-fill fs-2 text-dark"></i>
                                </div>
                            )}
                            <div className="aura-fan__select-side__card__info">
                                <h3 className="aura-fan__select-side__card__team-name" style={{ color: activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color }}>
                                    JUGADOR 1
                                </h3>
                            </div>
                            <i className="bi bi-chevron-right aura-fan__select-side__card__arrow" style={{ color: activeThemeData?.player1Color || DEFAULT_THEME_COLORS.player1Color }}></i>
                        </button>

                        {/* Botón Bando Jugador 2 (Visitante) */}
                        <button
                            type="button"
                            className="aura-fan__select-side__card aura-fan__select-side__card--p2"
                            onClick={() => handlePickSide('J2')}
                        >
                            {activeThemeData?.player2Avatar ? (
                                <img
                                    src={getResolvedMediaUrl(activeThemeData.player2Avatar)}
                                    alt="Avatar Jugador 2"
                                    className="aura-fan__select-side__card__avatar"
                                    onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                                />
                            ) : (
                                <div
                                    className="aura-fan__select-side__card__avatar d-flex align-items-center justify-content-center rounded-circle"
                                    style={{ backgroundColor: activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color }}
                                >
                                    <i className="bi bi-person-fill fs-2 text-dark"></i>
                                </div>
                            )}
                            <div className="aura-fan__select-side__card__info">
                                <h3 className="aura-fan__select-side__card__team-name" style={{ color: activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color }}>
                                    JUGADOR 2
                                </h3>
                            </div>
                            <i className="bi bi-chevron-right aura-fan__select-side__card__arrow" style={{ color: activeThemeData?.player2Color || DEFAULT_THEME_COLORS.player2Color }}></i>
                        </button>
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 2: SALA DE ESPERA (SINCRONIZADA CON EL ESTADIO)        */}
            {/* ============================================================= */}
            {currentEffectiveView === 'waiting' && (
                <main className="aura-fan__waiting">
                    <div className="aura-fan__waiting__avatar-container">
                        <div
                            className="aura-fan__waiting__energy-ring"
                            style={{ borderColor: teamPrimaryColor }}
                        />
                        {teamAvatarUrl ? (
                            <img
                                src={teamAvatarUrl}
                                alt="Avatar de Equipo"
                                className="aura-fan__waiting__avatar"
                                onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div
                                className="aura-fan__waiting__avatar d-flex align-items-center justify-content-center rounded-circle"
                                style={{ backgroundColor: teamPrimaryColor }}
                            >
                                <i className="bi bi-person-fill display-4 text-dark"></i>
                            </div>
                        )}
                    </div>

                    <div
                        className="aura-fan__waiting__team-badge"
                        style={{
                            backgroundColor: teamPrimaryColor,
                            borderColor: '#ffffff'
                        }}
                    >
                        {selectedPlayerSide === 'J1' ? 'JUGADOR 1' : 'JUGADOR 2'}
                    </div>

                    <h2 className="aura-fan__waiting__status-title">
                        {currentEngineState === 'cover'
                            ? '¡PREPÁRATE PARA EL COMBATE!'
                            : currentEngineState === 'game_ready'
                                ? 'La batalla está por comenzar...'
                                : 'ESPERANDO INICIO...'}
                    </h2>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 3: CUENTA REGRESIVA DINÁMICA (3, 2, 1)                  */}
            {/* ============================================================= */}
            {currentEffectiveView === 'countdown' && (
                <main className="aura-fan__countdown">
                    <div className="aura-fan__countdown__number" style={{ color: teamPrimaryColor }}>
                        {roundCountdownValue > 0 ? roundCountdownValue : '¡DALE!'}
                    </div>
                    <div className="aura-fan__countdown__label">
                        {roundCountdownValue > 0 ? '¡PREPÁRATE!' : '¡A FARMEAR AURA!'}
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 4: COMBATE EN VIVO (TAPPING GAME ULTRA-REACTIVO)       */}
            {/* ============================================================= */}
            {currentEffectiveView === 'playing' && (
                <main
                    className="aura-fan__combat"
                    onPointerDown={handlePointerDownTap}
                    onClick={handlePointerDownTap}
                >
                    {/* Personaje / Mascota de Bando reaccionando al toque */}
                    <div className="aura-fan__combat__avatar-preview">
                        {teamAvatarUrl ? (
                            <img
                                ref={activeCombatAvatarImageRef}
                                src={teamAvatarUrl}
                                alt="Avatar de Combate"
                                className="aura-fan__combat__avatar-img"
                                onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                            />
                        ) : null}
                    </div>

                    {/* Zona central y Botón táctil gigante */}
                    <div className="aura-fan__combat__tap-zone">
                        <button
                            type="button"
                            className={`aura-fan__combat__giant-button gfan__controls__tap ${selectedPlayerSide === 'J1' ? 'aura-fan__combat__giant-button--p1' : 'aura-fan__combat__giant-button--p2'}`}
                            aria-label="Farmear Aura por tu equipo"
                        >
                            <i className="bi bi-lightning-charge-fill aura-fan__combat__giant-button__icon" style={{ color: teamPrimaryColor }}></i>
                            <span className="aura-fan__combat__giant-button__text">¡FARMEA!</span>
                            <span className="aura-fan__combat__giant-button__subtext">Haz TAP TAP TAP en el botón</span>
                        </button>
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* VISTA 5: RESULTADOS DEL COMBATE (BANNER VICTORIA / DERROTA)   */}
            {/* ============================================================= */}
            {currentEffectiveView === 'finished' && (
                <main className="aura-fan__results">
                    <div
                        className={`aura-fan__results__card ${
                            roundWinnerResult === selectedPlayerSide
                                ? 'aura-fan__results__card--victory'
                                : (roundWinnerResult === 'Empate' || roundWinnerResult === 'tie')
                                    ? 'aura-fan__results__card--tie'
                                    : 'aura-fan__results__card--defeat'
                        }`}
                    >
                        {/* Avatar final */}
                        {(roundWinnerResult === selectedPlayerSide ? teamWinnerAvatarUrl : teamAvatarUrl) ? (
                            <img
                                src={roundWinnerResult === selectedPlayerSide ? teamWinnerAvatarUrl : teamAvatarUrl}
                                alt="Avatar Fin de Combate"
                                className="aura-fan__results__avatar"
                                onError={(imageErrorEvent) => { imageErrorEvent.target.style.display = 'none'; }}
                            />
                        ) : (
                            <div
                                className="aura-fan__results__avatar d-flex align-items-center justify-content-center rounded-circle mx-auto my-3"
                                style={{ width: '100px', height: '100px', backgroundColor: teamPrimaryColor }}
                            >
                                <i className="bi bi-trophy-fill fs-1 text-dark"></i>
                            </div>
                        )}

                        {/* Título de Victoria / Derrota / Empate */}
                        {roundWinnerResult === selectedPlayerSide ? (
                            <h2 className="aura-fan__results__outcome-title aura-fan__results__outcome-title--victory">
                                ¡Has ganado! 🏆
                            </h2>
                        ) : (roundWinnerResult === 'Empate' || roundWinnerResult === 'tie') ? (
                            <h2 className="aura-fan__results__outcome-title aura-fan__results__outcome-title--tie">
                                ¡Empate! ⚡
                            </h2>
                        ) : (
                            <h2 className="aura-fan__results__outcome-title aura-fan__results__outcome-title--defeat">
                                ¡Has perdido! 💥
                            </h2>
                        )}

                        <span className="aura-fan__results__next-round-hint">
                            Esperando a que inicie una nueva ronda...
                        </span>
                    </div>
                </main>
            )}

            {/* Pie de página con créditos */}
            <footer className="aura-fan__footer" style={{ fontSize: '20px', color: '#fff' }}>
                Hecho con ♥ por Interaktia | 2026
            </footer>
        </div>
    );
};

export const FarmeandoAuraFan = FightersFan;
export default FightersFan;
